import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../../../lib/apiClient';
import * as liveSessionApi from '../liveSessionApi';
import type {
  CurrentRolesResponse,
  LiveSessionPlayerResponse,
  LiveStateResponse,
  RoundHistoryResponse,
} from '../types';

export type UseLiveMatchArgs = {
  teamId: string | undefined;
  sessionId: string | undefined;
  matchId: string | undefined;
  pollIntervalMs?: number;
};

export type UseLiveMatchResult = {
  join: LiveSessionPlayerResponse | null;
  liveState: LiveStateResponse | null;
  roles: CurrentRolesResponse | null;
  history: RoundHistoryResponse | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
};

const emptyRoles = (roundNumber = 0): CurrentRolesResponse => ({
  roundId: '',
  roundNumber,
  assignments: [],
});

async function fetchRolesSafe(
  teamId: string,
  sessionId: string,
  matchId: string,
): Promise<CurrentRolesResponse> {
  try {
    return await liveSessionApi.getCurrentRoles(teamId, sessionId, matchId);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return emptyRoles();
    }
    throw err;
  }
}

export function useLiveMatch({
  teamId,
  sessionId,
  matchId,
  pollIntervalMs = 2000,
}: UseLiveMatchArgs): UseLiveMatchResult {
  const [join, setJoin] = useState<LiveSessionPlayerResponse | null>(null);
  const [liveState, setLiveState] = useState<LiveStateResponse | null>(null);
  const [roles, setRoles] = useState<CurrentRolesResponse | null>(null);
  const [history, setHistory] = useState<RoundHistoryResponse | null>(null);
  const [loading, setLoading] = useState(Boolean(teamId && sessionId && matchId));
  const [error, setError] = useState<Error | null>(null);
  const [tick, setTick] = useState(0);
  const joinedKeyRef = useRef<string | null>(null);

  const idsReady = Boolean(teamId && sessionId && matchId);
  const key = idsReady ? `${teamId}:${sessionId}:${matchId}` : null;

  const refetch = useCallback(() => setTick((n) => n + 1), []);

  // Join once per id set
  useEffect(() => {
    if (!teamId || !sessionId || !matchId || !key) {
      setJoin(null);
      setLiveState(null);
      setRoles(null);
      setHistory(null);
      setLoading(false);
      setError(null);
      joinedKeyRef.current = null;
      return;
    }

    if (joinedKeyRef.current === key) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    liveSessionApi
      .joinLiveSession(teamId, sessionId)
      .then((res) => {
        if (cancelled) return;
        joinedKeyRef.current = key;
        setJoin(res);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [teamId, sessionId, matchId, key, tick]);

  // Poll after join (or when tick forces refetch after successful join)
  useEffect(() => {
    if (!teamId || !sessionId || !matchId || !join) return;

    let cancelled = false;
    let generation = 0;

    async function pull() {
      const gen = ++generation;
      try {
        const [state, roleData, hist] = await Promise.all([
          liveSessionApi.getLiveState(teamId!, sessionId!, matchId!),
          fetchRolesSafe(teamId!, sessionId!, matchId!),
          liveSessionApi.getRoundHistory(teamId!, sessionId!, matchId!),
        ]);
        if (cancelled || gen !== generation) return;
        setLiveState(state);
        setRoles(roleData);
        setHistory(hist);
        setError(null);
        setLoading(false);
      } catch (err) {
        if (cancelled || gen !== generation) return;
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      }
    }

    pull();
    const id = window.setInterval(pull, pollIntervalMs);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [teamId, sessionId, matchId, join, pollIntervalMs, tick]);

  return { join, liveState, roles, history, loading, error, refetch };
}
